import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { ChevronLeft, Heart, Copy, Share2, MessageCircle, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Invite() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inviteData, setInviteData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchInviteMessage();
  }, []);

  const fetchInviteMessage = async () => {
    try {
      const response = await axios.get(`${API}/invite/message`);
      setInviteData(response.data);
    } catch (error) {
      console.error('Error fetching invite message:', error);
    }
  };

  const copyToClipboard = async () => {
    if (!inviteData) return;
    
    try {
      await navigator.clipboard.writeText(inviteData.message);
      setCopied(true);
      toast.success('Message copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Could not copy message');
    }
  };

  const shareNative = async () => {
    if (!inviteData) return;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'BlessBe - Daily Faith Companion',
          text: inviteData.message,
          url: inviteData.app_link
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard();
        }
      }
    } else {
      copyToClipboard();
    }
  };

  return (
    <div 
      className="min-h-screen bg-gradient-to-b from-[#FFFBF0] to-background"
      data-testid="invite-page"
    >
      {/* Header */}
      <header className="px-6 pt-8 pb-4 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="rounded-full"
          data-testid="back-button"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
      </header>

      <main className="px-6 space-y-8">
        {/* Hero */}
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 animate-gentle-pulse overflow-hidden">
            <img src="/images/logo.png" alt="BlessBe" className="w-20 h-20 object-cover" />
          </div>
          <h1 className="text-3xl md:text-4xl font-serif mb-3">
            Share the Blessing
          </h1>
          <p className="text-muted-foreground max-w-sm mx-auto">
            Invite someone you love to join you on this journey of faith and kindness
          </p>
        </div>

        {/* Message Preview */}
        <Card className="rounded-3xl border-border/40 shadow-soft">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground mb-2">Your invitation message:</p>
            <div className="bg-muted/50 rounded-2xl p-4">
              <p className="text-foreground leading-relaxed whitespace-pre-wrap">
                {inviteData?.message || 'Loading...'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button
            onClick={shareNative}
            className="w-full h-14 rounded-full text-lg shadow-soft hover:shadow-float transition-all"
            data-testid="share-button"
          >
            <Share2 className="w-5 h-5 mr-2" />
            Share with Someone
          </Button>

          <Button
            variant="outline"
            onClick={copyToClipboard}
            className="w-full h-12 rounded-full"
            data-testid="copy-button"
          >
            {copied ? (
              <>
                <Check className="w-5 h-5 mr-2 text-emerald-500" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-5 h-5 mr-2" />
                Copy Message
              </>
            )}
          </Button>
        </div>

        {/* Encouragement */}
        <Card className="rounded-2xl bg-gradient-to-r from-rose-50 to-pink-50 border-0">
          <CardContent className="p-6 text-center">
            <MessageCircle className="w-8 h-8 text-rose-400 mx-auto mb-3" />
            <p className="font-serif text-rose-800 mb-2">
              "A friend loves at all times"
            </p>
            <p className="text-sm text-rose-600">
              — Proverbs 17:17
            </p>
          </CardContent>
        </Card>

        {/* Skip */}
        <div className="text-center pb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/home')}
            className="text-muted-foreground"
            data-testid="skip-button"
          >
            Maybe later
          </Button>
        </div>
      </main>
    </div>
  );
}
